import type { WeldResult } from './types';

/**
 * Weld vertices that share a position within `tolerance` (a spatial-hash dedup).
 *
 * Models are often split into chunks with duplicated verts along the seams
 * (photogrammetry scans especially); welding stitches the topology so region-
 * growing (BFS over adjacency) can cross those seams. This affects TOPOLOGY ONLY — render buffers stay
 * untouched; `remap` carries per-vertex results (e.g. wave depths) back to the
 * original vertices.
 *
 * Cell size = tolerance, so any two points within tolerance are at most one cell
 * apart and are found by the 27-neighbour search.
 *
 * The cell key is a numeric spatial hash (Teschner et al.), NOT a string — at
 * multi-million-vertex scale, `cx + ',' + cy + ',' + cz` allocates ~27 short-lived
 * strings per vertex (hundreds of millions total) and GC-thrashes for tens of
 * seconds. Hash collisions only add far-away candidates to the per-bucket distance
 * test below, so the welding result is unchanged — just dramatically faster.
 */
export function weldVertices(positions: Float32Array, indices: Uint32Array, tolerance: number): WeldResult {
  const vertexCount = positions.length / 3;
  const remap = new Uint32Array(vertexCount);
  const invCell = 1 / Math.max(tolerance, 1e-9);
  const tol2 = tolerance * tolerance;

  const buckets = new Map<number, number[]>(); // hashed cell key -> welded vertex indices
  const welded: number[] = []; // flat xyz of welded vertices
  let weldedCount = 0;

  // Mix integer cell coords into one 32-bit key. Deterministic, so a vertex is
  // always found under its own cell's hash; collisions are filtered by distance.
  const hashOf = (cx: number, cy: number, cz: number) =>
    (Math.imul(cx, 73856093) ^ Math.imul(cy, 19349663) ^ Math.imul(cz, 83492791)) | 0;

  for (let v = 0; v < vertexCount; v++) {
    const x = positions[v * 3];
    const y = positions[v * 3 + 1];
    const z = positions[v * 3 + 2];
    const cx = Math.floor(x * invCell);
    const cy = Math.floor(y * invCell);
    const cz = Math.floor(z * invCell);

    let found = -1;
    for (let dx = -1; dx <= 1 && found < 0; dx++) {
      for (let dy = -1; dy <= 1 && found < 0; dy++) {
        for (let dz = -1; dz <= 1 && found < 0; dz++) {
          const bucket = buckets.get(hashOf(cx + dx, cy + dy, cz + dz));
          if (!bucket) continue;
          for (let b = 0; b < bucket.length; b++) {
            const wi = bucket[b];
            const ddx = welded[wi * 3] - x;
            const ddy = welded[wi * 3 + 1] - y;
            const ddz = welded[wi * 3 + 2] - z;
            if (ddx * ddx + ddy * ddy + ddz * ddz <= tol2) {
              found = wi;
              break;
            }
          }
        }
      }
    }

    if (found < 0) {
      found = weldedCount++;
      welded.push(x, y, z);
      const k = hashOf(cx, cy, cz);
      let b = buckets.get(k);
      if (!b) {
        b = [];
        buckets.set(k, b);
      }
      b.push(found);
    }
    remap[v] = found;
  }

  const weldedPositions = new Float32Array(welded);
  const weldedIndices = new Uint32Array(indices.length);
  for (let i = 0; i < indices.length; i++) weldedIndices[i] = remap[indices[i]];

  return { remap, weldedPositions, weldedIndices, weldedCount };
}
